import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { faPencilAlt, faUsers, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography, Paper, Box, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import { RouteSource } from '@monorepo/shared/src/graph/schema-generated-types-educator';
import useTeachingOrderStore from 'graph/state/query-hooks/use-teaching-order';
import useTeachingOrder from 'state/hooks/use-teaching-order';
import { getLetters } from '@monorepo/shared/src/util/text-util';
import HomeGridCard from './HomeGridCard';
import ClassroomSelector from './ClassroomSelector';
import useAssignments from 'state/hooks/use-assignments';
import {
  isLetterAssigned,
  areAllRequiredAssignmentsComplete,
} from 'components/modules/Assignments/utils';

const t = (key: string, options?) => translate('home', key, options);

const TeachingOrderGridRow: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [{ assignments }] = useAssignments();

  const [state] = useTeachingOrder();
  const [{ activeTeachingOrder }, { customOrderRefetch, teachingOrderRefetch }] =
    useTeachingOrderStore();

  const characters = activeTeachingOrder?.characters || [];

  const LetterIcon = ({ character }) => {
    const hasAssignment = isLetterAssigned(assignments, character);
    const allCompleted = areAllRequiredAssignmentsComplete(assignments, character);
    if (!allCompleted && !hasAssignment) return null;

    const styleClass = allCompleted ? classes.success : classes.primary;
    const icon = allCompleted ? faCheckCircle : faUsers;

    return (
      <span className={classes.letterIconSpan}>
        <FontAwesomeIcon className={styleClass} icon={icon} size={'sm'} />
      </span>
    );
  };

  const handleLetterClick = (bookCode) => () => {
    if (bookCode)
      navigate(`/letter-teaching/${bookCode}/letter-book`, {
        state: { from: RouteSource.DASHBOARD },
      });
  };

  useEffect(() => {
    customOrderRefetch();
    teachingOrderRefetch();
  }, [state]);

  return (
    <HomeGridCard
      paperProps={{ className: classes.gridCardClass }}
      style={{ width: '100%' }}
      xs={12}
    >
      <Box display="flex" flex={1} flexDirection="column" justifyContent="center">
        <Box className={classes.content}>
          <Box className={classes.classroomNameSection}>
            <ClassroomSelector />
            <Typography variant="subtitle1">{t('teachingOrderSubtitle')}</Typography>
          </Box>
          <Box className={classes.teachingOrderSection} display="flex" flexDirection="row">
            <Typography variant="h3" style={{ margin: '0 12px' }}>
              {t('teachingOrder', {
                teachingOrderName: activeTeachingOrder?.name?.replace('Order', ''),
              })}
            </Typography>
            <Link className={classes.buttonLike} to="/edit-teaching-order">
              <FontAwesomeIcon className={classes.iconClass} icon={faPencilAlt} />
              <Typography
                variant="subtitle1"
                color="primary"
                style={{ fontWeight: 600, lineHeight: '3.28125rem' }}
              >
                {t('editTeachingOrder')}
              </Typography>
            </Link>
          </Box>
        </Box>
        <Box className={classes.content} display="flex" flexWrap="wrap">
          {characters?.map((x, idx) => (
            <Paper
              key={`letter_char_button_${idx}`}
              className={classes.letterBox}
              variant="outlined"
            >
              <Button onClick={handleLetterClick(x.character)}>
                <Typography variant="h1" className={classes.letterButton}>
                  {getLetters(x?.character)}
                  <LetterIcon character={x?.character} />
                </Typography>
              </Button>
            </Paper>
          ))}
        </Box>
      </Box>
      <Box className={classes.legendDiv}>
        <Box className={classes.legendDivInner}>
          <FontAwesomeIcon className={classes.primary} icon={faUsers} size={'lg'} />
          <Typography className={classes.legendText} variant="subtitle1">
            {t('legendLetterAssigned')}
          </Typography>
        </Box>
        <Box className={classes.legendDivInner}>
          <FontAwesomeIcon className={classes.success} icon={faCheckCircle} size={'lg'} />
          <Typography className={classes.legendText} variant="subtitle1">
            {t('legendAssignmentCompleted')}
          </Typography>
        </Box>
      </Box>
    </HomeGridCard>
  );
};
const useStyles = makeStyles((theme: any) => ({
  buttonLike: {
    display: 'flex',
    margin: '0px 12px',
    textDecoration: 'none',
    color: theme.palette.primary.main,
    lineHeight: '3.28125rem',
  },
  classroomNameSection: {
    float: 'left',
  },
  gridCardClass: {
    display: 'flex',
    minHeight: 220,
    margin: theme.spacing(4),
  },
  iconClass: {
    alignSelf: 'center',
    margin: '0px 5px 8px',
  },
  letterBox: {
    marginLeft: '15px',
    marginBottom: '15px',
  },
  letterButton: {
    minWidth: '80px !important',
    minHeight: '60px !important',
    fontSize: '24px',
    color: 'black',
  },
  letterIconSpan: {
    padding: 0,
    margin: 0,
    position: 'absolute',
    top: 28,
    left: 5,
  },
  legendDiv: {
    display: 'flex',
    alignItems: 'center',
    margin: '0 15px 15px 15px',
  },
  legendDivInner: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '15px',
  },
  legendText: {
    marginLeft: 7,
  },
  success: {
    color: theme.palette.success.main,
  },
  primary: {
    color: theme.palette.primary.main,
  },
  content: {
    margin: '0 15px 15px',
  },
  teachingOrderSection: {
    float: 'right',
    height: '3.75rem',
    lineHeight: '3.75rem',
    '& p': {
      lineHeight: '3.75rem',
    },
    '& h6': {
      lineHeight: '3.75rem',
      paddingLeft: '5px',
    },
  },
}));

export default TeachingOrderGridRow;
